import React from 'react';
import PropTypes from 'prop-types';
import { companyType } from '#/prop-types/otp-ui';
import {
  BIKETOWN_COMPANY_1,
  BUS_LAYER,
  MAX_LAYER,
  WES_LAYER,
  PSC_LAYER,
} from '#/map/constants';
import {
  BICYCLE_RENT_MODE,
  MICROMOBILITY_RENT_MODE,
  CAR_RENT_MODE,
  PARK_AND_RIDE_MODE,
} from '#/shared/constants';
import featureFlags from '#/shared/feature-flags';
import GTM_HANDLES from '#/shared/gtm-handles';
import SectionHeader from './SectionHeader';
import * as Styled from './styled';

const { mapLibre, themes } = featureFlags;

function CheckboxWithLogo({ logo, checked, label, onCheckedChange, dataGtm }) {
  return (
    <Styled.CheckboxWrapper>
      {logo}
      <Styled.MapSettingsCheckbox
        labelPosition="left"
        checked={checked}
        label={label}
        onCheckedChange={onCheckedChange}
        dataGtm={dataGtm}
      />
    </Styled.CheckboxWrapper>
  );
}

function MapLayerControls({
  currentTheme,
  currentBaseLayer,
  modes,
  configCompanies,
  selectedCompanies,
  routeLayers,
  realtimeVehiclesToggled,
  alwaysShowMapControls,
  setTheme,
  setBaseLayerStreets,
  setBaseLayerAerials,
  setBaseLayerRoutes,
  setBaseLayerRoutesAerials,
  setBaseLayerRoutesThreeDimensional,
  setBaseLayerThreeDimensional,
  toggleRentalCompany,
  toggleParkAndRide,
  toggleRealtimeVehicles,
  toggleRouteLayers,
  toggleAlwaysShowMapControls,
}) {
  const layerOptions = [
    {
      label: { stringId: 'map.streetslayer' },
      value: 'streets',
      dataGtm: GTM_HANDLES.layerButton.appearance,
    },
    {
      label: { stringId: 'map.aerialslayer' },
      value: 'aerials',
      dataGtm: GTM_HANDLES.layerButton.appearance,
    },
  ];

  if (mapLibre) {
    layerOptions.push(
      {
        label: { stringId: 'map.routeslayer' },
        value: 'routes',
        dataGtm: GTM_HANDLES.layerButton.appearance,
      },
      {
        label: { stringId: 'map.routes+aerialslayer' },
        value: 'routes + aerials',
        dataGtm: GTM_HANDLES.layerButton.appearance,
      },
      {
        label: { stringId: 'map.3Dlayer' },
        value: 'three dimensional',
        dataGtm: GTM_HANDLES.layerButton.appearance,
      }
    );
  }

  const themeOptions = [
    {
      label: { stringId: 'map.legacy-theme' },
      value: 'Legacy',
      dataGtm: GTM_HANDLES.layerButton.appearance,
    },
    {
      label: { stringId: 'map.light-theme' },
      value: 'Light',
      dataGtm: GTM_HANDLES.layerButton.appearance,
    },
    {
      label: { stringId: 'map.dark-theme' },
      value: 'Dark',
      dataGtm: GTM_HANDLES.layerButton.appearance,
    },
  ];
  return (
    <Styled.ControlsInner>
      <Styled.ControlsBody>
        <Styled.ControlsSection>
          <SectionHeader labelId="map.mapsettings" Icon={Styled.MapIcon} />
          <Styled.LayerToggleCollection>
            {themes && (
              <Styled.MapSettingsRadioGroup
                customFieldsetProvided
                radioButtons={themeOptions}
                initialValue={currentTheme}
                name="themes"
                labelPosition="left"
                onChange={value => {
                  setTheme(value);
                }}
              />
            )}
            <Styled.MapSettingsRadioGroup
              customFieldsetProvided
              radioButtons={layerOptions}
              initialValue={currentBaseLayer}
              name="map-layers"
              labelPosition="left"
              onChange={value => {
                switch (value) {
                  case 'streets':
                    setBaseLayerStreets();
                    break;
                  case 'aerials':
                    setBaseLayerAerials();
                    break;
                  case 'routes':
                    setBaseLayerRoutes();
                    break;
                  case 'routes + aerials':
                    setBaseLayerRoutesAerials();
                    break;
                  case 'three dimensional':
                    setBaseLayerThreeDimensional();
                    break;
                  case 'routes + three dimensional':
                    setBaseLayerRoutesThreeDimensional();
                    break;
                  default:
                    setBaseLayerStreets();
                    break;
                }
              }}
            />
          </Styled.LayerToggleCollection>
        </Styled.ControlsSection>
        <Styled.ControlsSection>
          <SectionHeader labelId="map.mapcontrols" Icon={Styled.MapIcon} />
          <Styled.GridSection>
            <CheckboxWithLogo
              checked={alwaysShowMapControls}
              onCheckedChange={() => toggleAlwaysShowMapControls()}
              dataGtm={GTM_HANDLES.map.alwaysShowMapControls}
              label={{ stringId: 'map.alwaysshowmapcontrols' }}
              logo={<Styled.MapControlsIcon aria-hidden="true" />}
            />
          </Styled.GridSection>
        </Styled.ControlsSection>
        <Styled.ControlsSection>
          <SectionHeader labelId="map.transitroutes" Icon={Styled.MapIcon} />
          <Styled.GridSection>
            <CheckboxWithLogo
              checked={routeLayers.includes(BUS_LAYER)}
              onCheckedChange={() => toggleRouteLayers(BUS_LAYER)}
              dataGtm={GTM_HANDLES.menu.modeLinkBus}
              label={{ stringId: 'menu.buses' }}
              logo={<Styled.TriMetButtonIcon aria-hidden="true" />}
            />
            <CheckboxWithLogo
              checked={routeLayers.includes(MAX_LAYER)}
              onCheckedChange={() => toggleRouteLayers(MAX_LAYER)}
              dataGtm={GTM_HANDLES.menu.modeLinkMax}
              label={{ stringId: 'menu.tram' }}
              logo={<Styled.TriMetButtonIcon aria-hidden="true" />}
            />
            <CheckboxWithLogo
              checked={routeLayers.includes(WES_LAYER)}
              onCheckedChange={() => toggleRouteLayers(WES_LAYER)}
              dataGtm={GTM_HANDLES.menu.modeLinkWes}
              label={{ stringId: 'menu.rail' }}
              logo={<Styled.TriMetButtonIcon aria-hidden="true" />}
            />
            <CheckboxWithLogo
              checked={routeLayers.includes(PSC_LAYER)}
              onCheckedChange={() => toggleRouteLayers(PSC_LAYER)}
              dataGtm={GTM_HANDLES.menu.modeLinkStreetcar}
              label={{ stringId: 'menu.streetcar' }}
              logo={<Styled.StreetcarButtonIcon aria-hidden="true" />}
            />
            <CheckboxWithLogo
              checked={modes.includes(PARK_AND_RIDE_MODE)}
              onCheckedChange={() => toggleParkAndRide()}
              dataGtm={GTM_HANDLES.layerButton.transit}
              label={{ stringId: 'map.parkandridelayer' }}
              logo={<Styled.ParkRideButtonIcon aria-hidden="true" />}
            />
            <CheckboxWithLogo
              checked={realtimeVehiclesToggled}
              onCheckedChange={() => toggleRealtimeVehicles()}
              dataGtm={GTM_HANDLES.layerButton.transit}
              label={{ stringId: 'map.realtimevehicleslayer' }}
              logo={<Styled.TransitTrackerButtonIcon aria-hidden="true" />}
            />
          </Styled.GridSection>
        </Styled.ControlsSection>
        <Styled.ControlsSection>
          <SectionHeader
            labelId="map.bicyclerental"
            Icon={Styled.BicycleIcon}
          />
          <Styled.GridSection>
            <CheckboxWithLogo
              checked={modes.includes(BICYCLE_RENT_MODE)}
              onCheckedChange={() => toggleRentalCompany(BIKETOWN_COMPANY_1)}
              dataGtm={GTM_HANDLES.layerButton.bikes}
              label={{ stringId: 'map.bicyclerentallayer' }}
              logo={
                <Styled.RentalCompanyIcon
                  company={BIKETOWN_COMPANY_1}
                  aria-hidden="true"
                />
              }
            />
          </Styled.GridSection>
        </Styled.ControlsSection>

        {configCompanies.some(company =>
          company.modes.includes(MICROMOBILITY_RENT_MODE)
        ) && (
          <Styled.ControlsSection>
            <SectionHeader
              labelId="map.escooterrental"
              Icon={Styled.ScooterIcon}
            />
            <Styled.GridSection>
              {configCompanies
                .filter(company =>
                  company.modes.includes(MICROMOBILITY_RENT_MODE)
                )
                .map(company => (
                  <CheckboxWithLogo
                    key={company.id}
                    checked={selectedCompanies.includes(company.id)}
                    onCheckedChange={() => toggleRentalCompany(company.id)}
                    dataGtm={GTM_HANDLES.layerButton.scooters}
                    label={{ untranslatedEnglishString: company.label }}
                    logo={
                      <Styled.RentalCompanyIcon
                        aria-hidden="true"
                        company={company.id}
                      />
                    }
                  />
                ))}
            </Styled.GridSection>
          </Styled.ControlsSection>
        )}

        {configCompanies.some(company =>
          company.modes.includes(CAR_RENT_MODE)
        ) && (
          <Styled.ControlsSection>
            <SectionHeader labelId="map.carrental" Icon={Styled.CarIcon} />
            <Styled.GridSection>
              {configCompanies
                .filter(company => company.modes.includes(CAR_RENT_MODE))
                .map(company => (
                  <CheckboxWithLogo
                    key={company.id}
                    checked={selectedCompanies.includes(company.id)}
                    onCheckedChange={() => toggleRentalCompany(company.id)}
                    dataGtm={GTM_HANDLES.layerButton.car}
                    label={{ untranslatedEnglishString: company.label }}
                    logo={
                      <Styled.RentalCompanyIcon
                        aria-hidden="true"
                        company={company.id}
                      />
                    }
                  />
                ))}
            </Styled.GridSection>
          </Styled.ControlsSection>
        )}
      </Styled.ControlsBody>
    </Styled.ControlsInner>
  );
}

MapLayerControls.propTypes = {
  /** A set of all rental companies */
  configCompanies: PropTypes.arrayOf(companyType).isRequired,
  /** The currently selected application theme */
  currentTheme: PropTypes.string.isRequired,
  /** The currently selected map base layer */
  currentBaseLayer: PropTypes.string.isRequired,
  /** The rental companies that are toggled on the map */
  selectedCompanies: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** The modes that are current active on the map */
  modes: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** The list of route overlays that are current active on the map */
  routeLayers: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Whether realtime vehicles are toggled on the map */
  realtimeVehiclesToggled: PropTypes.bool.isRequired,
  /** Whether the map controls are always shown */
  alwaysShowMapControls: PropTypes.bool.isRequired,
  /** Function to set the application theme */
  setTheme: PropTypes.func.isRequired,
  /** Function to set the map base layer to Streets */
  setBaseLayerStreets: PropTypes.func.isRequired,
  /** Function to set the map base layer to Aerials */
  setBaseLayerAerials: PropTypes.func.isRequired,
  /** Function to set the map base layer to Routes */
  setBaseLayerRoutes: PropTypes.func,
  /** Function to set the map base layer to Routes + Aerials */
  setBaseLayerRoutesAerials: PropTypes.func,
  /** Function to set the map base layer to Routes + 3D */
  setBaseLayerRoutesThreeDimensional: PropTypes.func,
  /** Function to set the map base layer to 3D */
  setBaseLayerThreeDimensional: PropTypes.func,
  /** Toggles the rental company (e.g. bike, scooter, car) just for the map */
  toggleRentalCompany: PropTypes.func.isRequired,
  /** Toggles the park and ride mode for just the map */
  toggleParkAndRide: PropTypes.func.isRequired,
  /** Toggles the realtime vehicles layer on and off */
  toggleRealtimeVehicles: PropTypes.func.isRequired,
  /** Toggles various routeLayers on and off */
  toggleRouteLayers: PropTypes.func.isRequired,
  /** Toggles the map controls always shown */
  toggleAlwaysShowMapControls: PropTypes.func.isRequired,
};

MapLayerControls.defaultProps = {
  setBaseLayerRoutes: () => null,
  setBaseLayerRoutesAerials: () => null,
  setBaseLayerRoutesThreeDimensional: () => null,
  setBaseLayerThreeDimensional: () => null,
};

CheckboxWithLogo.propTypes = {
  logo: PropTypes.element.isRequired,
  checked: PropTypes.bool.isRequired,
  label: PropTypes.shape({
    stringId: PropTypes.string,
    untranslatedEnglishString: PropTypes.string,
  }).isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  dataGtm: PropTypes.string.isRequired,
};

export default MapLayerControls;
